// featureWindowRegistry — declares, per surface KEY (from
// featureWindowSurface.ts's resolver), the ordered feature-tree ITEMS that
// open as JAWS-style floating windows in the viewer. Each item has a stable
// `key` (used by useFeatureWindows), a `label` (shown in the tree AND the
// window header), and a `render` that produces the window body.
//
// This is a DATA registry (Pillar 3 — a concern-aligned config surface, like
// implicitPrimitives.ts). Every domain that has editable parameters is
// registered here — the feature tree itself never fills up with raw input
// fields again; it only ever shows tree rows that open a window. Enforced by
// `npm run lint:feature-tree` (scripts/check-feature-tree.mjs).
//
// Two shapes of entry:
//   - Fixed surfaces get a bespoke split when the source content naturally
//     divides into more than one group (rocket nozzle: inputs vs computed
//     performance; tubeframe joint: dimensions vs joint). Everything else
//     wraps its EXISTING content component, unchanged, in a single
//     "Parameters" window — there's no value in inventing an artificial
//     second window where the source UI doesn't already have one.
//   - Primitive-driven surfaces (`primitive/<kind>`) are generated in a loop
//     over every key in IMPLICIT_PRIMITIVE_LABELS (except 'lattice-fill',
//     which has its own dedicated converter entry) — all of them render the
//     same `ImplicitPrimitivesContent`, which already switches on the active
//     `implicitPrimitive` internally.

import { createElement, type ReactNode } from 'react';
import { IMPLICIT_PRIMITIVE_LABELS } from '@/config/implicitPrimitives';
import {
  TubeframeDimensionsFields,
  TubeframeJointFields,
} from './TubeframeFields';
import {
  NozzleParameterFields,
  RocketPerformanceFields,
} from './RocketNozzleFields';
import { AirContent } from './AirContent';
import { LatticeConverterContent } from './LatticeConverterContent';
import { ImplicitPrimitivesContent } from './ImplicitPrimitivesContent';
import { SPACEFRAME_FEATURE_WINDOWS } from './spaceframeWindows';
import { CROSSKART_FEATURE_WINDOWS } from '@/components/crosskart/crosskartWindows';

export interface FeatureWindowItem {
  /** Stable key for useFeatureWindows (open/popped-out tracking). */
  key: string;
  /** Tree row + window header label. */
  label: string;
  /** Optional window width in px (default 320). */
  width?: number;
  /** Window body. `enabled` mirrors the domain-enabled state (disables inputs
   *  when the domain isn't active). */
  render: (enabled: boolean) => ReactNode;
}

// Keyed by the resolver's surface key (see featureWindowSurface.ts). Absent
// entry ⇒ no window for that surface (the caller falls back to its own
// non-windowed content — the plain text-to-cad prompt box, or, with the flag
// off, the legacy single pane).
export const FEATURE_WINDOW_REGISTRY: Record<string, FeatureWindowItem[]> = {
  'space/rocket-nozzle': [
    {
      key: 'nozzle-parameters',
      label: 'Nozzle Parameters',
      render: (enabled) => createElement(NozzleParameterFields, { enabled }),
    },
    {
      key: 'rocket-performance',
      label: 'Rocket Performance',
      render: () => createElement(RocketPerformanceFields),
    },
  ],
  'land/tubeframe-joint': [
    {
      key: 'tubeframe-dimensions',
      label: 'Dimensions',
      // createElement (not a bare call) so the component mounts as a real
      // element — it uses hooks, which a plain function call would break.
      render: (enabled) =>
        createElement(TubeframeDimensionsFields, { enabled }),
    },
    {
      key: 'tubeframe-joint',
      label: 'Joint',
      render: (enabled) => createElement(TubeframeJointFields, { enabled }),
    },
  ],
  // Land → Spaceframe Chassis: 10 windows, lazy (see spaceframeWindows.ts).
  'land/spaceframe': SPACEFRAME_FEATURE_WINDOWS,
  // Land → Cross Kart (2 Seater): 15 windows, lazy (see crosskart/crosskartWindows.ts).
  'land/cross-kart': CROSSKART_FEATURE_WINDOWS,
  'air/nasa-supersonic-inlet': [
    {
      key: 'inlet-parameters',
      label: 'Inlet Parameters',
      render: (enabled) => createElement(AirContent, { enabled }),
    },
  ],
  'default/nasa-unitcell-converter': [
    {
      key: 'lattice-converter-parameters',
      label: 'Parameters',
      render: (enabled) => createElement(LatticeConverterContent, { enabled }),
    },
  ],
};

// Primitive-driven surfaces: one "Parameters" window per ImplicitPrimitive
// kind, all wrapping the SAME ImplicitPrimitivesContent (which already
// switches on the active primitive internally) — covers the Implicit
// Primitives library, NASA Lattice Primitives, Land → Wheels, and
// Space → Heat Exchanger uniformly. 'lattice-fill' is excluded — it has its
// own dedicated converter entry above, with its own upload-driven UI.
for (const kind of Object.keys(IMPLICIT_PRIMITIVE_LABELS)) {
  if (kind === 'lattice-fill') continue;
  FEATURE_WINDOW_REGISTRY[`primitive/${kind}`] = [
    {
      key: 'parameters',
      label: 'Parameters',
      render: (enabled) =>
        createElement(ImplicitPrimitivesContent, { enabled }),
    },
  ];
}

/** The ordered feature items for a surface key, or [] if none are registered. */
export function featureWindowItems(
  key: string | null | undefined,
): FeatureWindowItem[] {
  return key ? (FEATURE_WINDOW_REGISTRY[key] ?? []) : [];
}
